import { useState, useEffect } from "react";
import {
  Search,
  ChevronRight,
  MessageSquare,
  Paperclip,
  Clock,
  ArrowLeft,
  X,
  FileText,
  Calendar,
  Tag,
} from "lucide-react";
import { SupportTicketApi } from "../../api/support/service";

const mockTickets = [
  {
    id: "SM-1043",
    subject: "Unable to export project report",
    category: "Technical Issue",
    status: "in_progress",
    priority: "high",
    createdAt: "2024-12-20",
    updatedAt: "2024-12-22",
    messages: [
      {
        id: "1",
        sender: "user",
        senderName: "You",
        content:
          "I'm trying to export a project report but the download keeps failing. I've tried multiple browsers.",
        timestamp: "Dec 20, 2:30 PM",
      },
      {
        id: "2",
        sender: "support",
        senderName: "Priya from SiteMaster",
        content:
          "Hi! Thank you for reaching out. I understand how frustrating this must be. Could you please share which project you're trying to export and what error message (if any) you see?",
        timestamp: "Dec 21, 10:15 AM",
      },
      {
        id: "3",
        sender: "user",
        senderName: "You",
        content:
          "It's the 'Green Valley Villas' project. The page just shows 'Export failed' without any other details.",
        timestamp: "Dec 21, 11:00 AM",
      },
      {
        id: "4",
        sender: "support",
        senderName: "Priya from SiteMaster",
        content:
          "Thank you for the details! I've identified the issue – it seems there's a large attachment in one of the entries causing the timeout. Our team is working on a fix. In the meantime, I can generate the report for you manually. Would that work?",
        timestamp: "Dec 22, 9:30 AM",
      },
    ],
    attachments: ["screenshot.png"],
  },
  {
    id: "SM-1038",
    subject: "How to add multiple bank accounts?",
    category: "Finance & Payments",
    status: "resolved",
    priority: "low",
    createdAt: "2024-12-15",
    updatedAt: "2024-12-16",
    messages: [
      {
        id: "1",
        sender: "user",
        senderName: "You",
        content:
          "Is there a way to add more than one bank account for vendor payments?",
        timestamp: "Dec 15, 4:00 PM",
      },
      {
        id: "2",
        sender: "support",
        senderName: "Rahul from SiteMaster",
        content:
          "Yes! You can add multiple bank accounts. Go to Settings > Finance > Bank Accounts and click 'Add Account'. You can set one as default for automatic selection. Hope this helps!",
        timestamp: "Dec 16, 9:00 AM",
      },
    ],
    attachments: [],
  },
  {
    id: "SM-1025",
    subject: "Request: Dark mode for mobile app",
    category: "Feature Request",
    status: "open",
    priority: "low",
    createdAt: "2024-12-10",
    updatedAt: "2024-12-10",
    messages: [
      {
        id: "1",
        sender: "user",
        senderName: "You",
        content:
          "Would love to see a dark mode option for the mobile app. Using it on site at night can be harsh on the eyes.",
        timestamp: "Dec 10, 8:30 PM",
      },
    ],
    attachments: [],
  },
];

const statusConfig = {
  open: {
    label: "Open",
    styles: "bg-blue-100 text-blue-800 border-blue-200",
    dot: "#3b82f6",
  },
  created: {
    label: "Open",
    styles: "bg-blue-100 text-blue-800 border-blue-200",
    dot: "#3b82f6",
  },
  in_progress: {
    label: "In Progress",
    styles: "bg-yellow-100 text-yellow-800 border-yellow-200",
    dot: "#eab308",
  },
  waiting: {
    label: "Waiting on You",
    styles: "bg-orange-100 text-orange-800 border-orange-200",
    dot: "#f97316",
  },
  resolved: {
    label: "Resolved",
    styles: "bg-green-100 text-green-800 border-green-200",
    dot: "#22c55e",
  },
  closed: {
    label: "Closed",
    styles: "bg-gray-100 text-gray-800 border-gray-200",
    dot: "#6b7280",
  },
  on_hold: {
    label: "On Hold",
    styles: "bg-purple-100 text-purple-800 border-purple-200",
    dot: "#a855f7",
  },
};

const normalizeStatus = (apiStatus) => {
  if (!apiStatus) return "open";
  const normalized = apiStatus.toLowerCase().replace(/\s+/g, "_");
  return normalized;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toISOString().split("T")[0];
};

const normalizeAttachments = (attachments) => {
  if (!attachments) return [];
  if (Array.isArray(attachments)) {
    return attachments.filter(Boolean);
  }
  if (typeof attachments === "string") {
    const trimmed = attachments.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
      } catch (error) {
        return [trimmed];
      }
    }
    return [trimmed];
  }
  return [];
};

const getAttachmentLabel = (fileUrl) => {
  if (!fileUrl || typeof fileUrl !== "string") return "Attachment";
  try {
    const pathWithoutQuery = fileUrl.split("?")[0];
    const fileName = pathWithoutQuery.substring(pathWithoutQuery.lastIndexOf("/") + 1);
    return decodeURIComponent(fileName || "Attachment");
  } catch (error) {
    return "Attachment";
  }
};

const isImageFile = (fileUrl) => {
  if (!fileUrl || typeof fileUrl !== "string") return false;
  const ext = fileUrl.split("?")[0].split(".").pop().toLowerCase();
  return ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"].includes(ext);
};

export function MyTicketsTab() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const organizationId = JSON.parse(localStorage.getItem("organization"))?.id;

        if (!organizationId) {
          throw new Error("Organization not found. Please log in again.");
        }

        const response = await SupportTicketApi.getTickets(organizationId);

        if (response && response.status && response.data) {
          const mappedTickets = response.data.map((ticket) => ({
            id: ticket.ticket_number,
            apiId: ticket.id,
            subject: ticket.issue_header,
            category: ticket.category || "",
            status: normalizeStatus(ticket.status),
            priority: (ticket.priority || "low").toLowerCase(),
            createdAt: formatDate(ticket.created_on),
            updatedAt: formatDate(ticket.modified_on),
            rawUpdatedAt: ticket.modified_on || ticket.created_on || "",
            messages: [],
            attachments: normalizeAttachments(ticket.attachments),
            description: ticket.issue_description || "",
            createdBy: ticket.created_by,
            onHoldJustification: ticket.on_hold_justification,
          }));

          setTickets(mappedTickets);
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (err) {
        console.error("Error fetching tickets:", err);
        setError(err.message || "Failed to load tickets");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTickets();
  }, []);

  const handleCloseSidebar = () => {
    setSidebarVisible(false);
    setTimeout(() => setSelectedTicket(null), 300);
  };

  useEffect(() => {
    if (selectedTicket) {
      setTimeout(() => setSidebarVisible(true), 10);
    } else {
      setSidebarVisible(false);
    }
  }, [selectedTicket]);

  const filteredTickets = tickets
    .filter(
      (ticket) =>
        (ticket.subject || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (ticket.id || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (ticket.category || "").toLowerCase().includes(searchQuery.toLowerCase()),
    )
    .sort((a, b) => new Date(b.rawUpdatedAt) - new Date(a.rawUpdatedAt));

  const handleSendReply = () => {
    if (!replyText.trim()) return;
    setReplyText("");
  };

  /*
  if (selectedTicket) {
    const status = statusConfig[selectedTicket.status] || statusConfig.open;

    return (
      <div className="w-full px-6 animate-fade-in">
        <button
          onClick={() => setSelectedTicket(null)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-6 group"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium">Back to tickets</span>
        </button>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 gap-4">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <span className="font-mono text-sm text-gray-600 font-medium">
                {selectedTicket.id}
              </span>
              <span
                className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold shadow-sm ${status.styles}`}
              >
                {status.label}
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              {selectedTicket.subject}
            </h2>
            <p className="text-sm text-gray-600">
              {selectedTicket.category ? `${selectedTicket.category} • ` : ""}Created {selectedTicket.createdAt}
            </p>
          </div>
        </div>

        <div className="space-y-4 mb-6">
          {selectedTicket.messages.map((message, index) => (
            <div
              key={message.id}
              className={`p-4 rounded-xl animate-fade-in shadow-sm ${message.sender === "user"
                ? "bg-purple-50 border border-purple-100 ml-0 sm:ml-8"
                : "bg-gray-100 border border-gray-200 mr-0 sm:mr-8"
                }`}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-sm text-gray-900">
                  {message.senderName}
                </span>
                <span className="text-xs text-gray-500">
                  {message.timestamp}
                </span>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                {message.content}
              </p>
            </div>
          ))}
        </div>

        {selectedTicket.attachments.length > 0 && (
          <div className="mb-6">
            <p className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Paperclip className="h-4 w-4" />
              Attachments
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedTicket.attachments.map((file, index) => (
                <a
                  key={index}
                  href={file}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer shadow-sm"
                >
                  {getAttachmentLabel(file)}
                </a>
              ))}
            </div>
          </div>
        )}

        // Reply Box — commented out
        // {selectedTicket.status !== "closed" &&
        //   selectedTicket.status !== "resolved" && (
        //     <div className="border-t border-gray-200 pt-6 mt-6">
        //       <textarea
        //         placeholder="Type your reply..."
        //         value={replyText}
        //         onChange={(e) => setReplyText(e.target.value)}
        //         className="w-full rounded-xl min-h-[120px] border border-gray-300 bg-white px-4 py-3 text-sm placeholder:text-gray-400 resize-y mb-3 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-shadow shadow-sm"
        //       />
        //       <div className="flex flex-col sm:flex-row justify-end gap-2">
        //         <button className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium hover:bg-gray-50 transition-colors shadow-sm">
        //           <Paperclip className="h-4 w-4" />
        //           Attach File
        //         </button>
        //         <button
        //           onClick={handleSendReply}
        //           disabled={!replyText.trim()}
        //           className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#3B0069] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#2d0051] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md active:scale-[0.98]"
        //         >
        //           <MessageSquare className="h-4 w-4" />
        //           Send Reply
        //         </button>
        //       </div>
        //     </div>
        //   )}

        // Activity Log — commented out
        // <div className="mt-8 pt-6 border-t border-gray-200">
        //   <p className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
        //     <Clock className="h-4 w-4" />
        //     Activity Log
        //   </p>
        //   <div className="space-y-3">
        //     <div className="flex items-center gap-3 text-sm">
        //       <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
        //       <span className="text-gray-600 flex-1">Ticket created</span>
        //       <span className="text-gray-500 text-xs">
        //         {selectedTicket.createdAt}
        //       </span>
        //     </div>
        //     <div className="flex items-center gap-3 text-sm">
        //       <div className="w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0" />
        //       <span className="text-gray-600 flex-1">
        //         Status changed to {status.label}
        //       </span>
        //       <span className="text-gray-500 text-xs">
        //         {selectedTicket.updatedAt}
        //       </span>
        //     </div>
        //   </div>
        // </div>
      </div>
    );
  }
  */

  return (
    <div className="w-full relative">
      <div className="w-full space-y-6 animate-fade-in">
        {isLoading && (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-4"></div>
            <p className="text-gray-600">Loading tickets...</p>
          </div>
        )}

        {error && !isLoading && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
            <p className="text-red-800 font-medium mb-2">Failed to load tickets</p>
            <p className="text-red-600 text-sm">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
            >
              Retry
            </button>
          </div>
        )}

        {!isLoading && !error && (
          <>
            <div className="relative max-w-2xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search tickets by ID, subject, or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 h-12 w-full rounded-xl border border-gray-300 bg-white text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-shadow shadow-sm"
              />
            </div>

            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Ticket ID
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Subject
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 hidden md:table-cell">
                        Category
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Status
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 hidden lg:table-cell">
                        Updated
                      </th>
                      <th className="px-4 py-3 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTickets.map((ticket) => {
                      const status = statusConfig[ticket.status] || statusConfig.open;
                      return (
                        <tr
                          key={ticket.id}
                          className="border-b border-gray-200 last:border-0 hover:bg-gray-50 transition-all duration-200 cursor-pointer animate-fade-in group"
                          onClick={() => setSelectedTicket(ticket)}
                        >
                          <td className="px-4 py-4">
                            <span className="font-mono text-sm text-gray-900 font-medium">
                              {ticket.id}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex flex-col">
                              <span className="text-sm text-gray-900 font-semibold group-hover:text-[#3B0069] transition-colors">
                                {ticket.subject}
                              </span>
                              {ticket.attachments.length > 0 && (
                                <span className="mt-1 inline-flex items-center gap-1 text-xs text-[#3B0069] font-medium">
                                  <Paperclip className="h-3 w-3" />
                                  {ticket.attachments.length} attachment{ticket.attachments.length > 1 ? "s" : ""}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 hidden md:table-cell">
                            <span className="text-sm text-gray-600">
                              {ticket.category}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <span
                              className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${status.styles}`}
                            >
                              {status.label}
                            </span>
                          </td>
                          <td className="px-4 py-4 hidden lg:table-cell">
                            <span className="text-sm text-gray-600">
                              {ticket.updatedAt}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {filteredTickets.length === 0 && (
                <div className="text-center py-16 animate-fade-in">
                  <div className="mb-4">
                    <MessageSquare className="h-12 w-12 text-gray-300 mx-auto" />
                  </div>
                  <p className="text-lg text-gray-600 font-medium mb-1">
                    No tickets found
                  </p>
                  <p className="text-sm text-gray-500">
                    {searchQuery
                      ? "Try a different search term"
                      : "You haven't created any tickets yet"}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Sidebar Modal — starts below the 60px top bar */}
      {selectedTicket && (
        <>
          {/* Backdrop */}
          <div
            onClick={handleCloseSidebar}
            style={{
              position: "fixed",
              top: 60,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.32)",
              zIndex: 40,
              opacity: sidebarVisible ? 1 : 0,
              transition: "opacity 0.3s ease",
            }}
          />

          {/* Sidebar Panel */}
          <div
            style={{
              position: "fixed",
              top: 65,
              right: 0,
              height: "calc(100vh - 65px)",
              width: "100%",
              maxWidth: "560px",
              background: "#ffffff",
              zIndex: 50,
              display: "flex",
              flexDirection: "column",
              boxShadow: "-12px 0 48px rgba(59,0,105,0.18)",
              transform: sidebarVisible ? "translateX(0)" : "translateX(100%)",
              transition: "transform 0.32s cubic-bezier(0.4, 0, 0.2, 1)",
              borderRadius: "16px 0 0 0",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <div
              style={{
                background: "linear-gradient(145deg, #6C2BD7 0%, #4a1a9a 45%, #3B0069 100%)",
                padding: "28px 24px 24px",
                position: "relative",
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: -40,
                  right: -40,
                  width: 140,
                  height: 140,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.06)",
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: -20,
                  left: -20,
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.04)",
                  pointerEvents: "none",
                }}
              />

              {/* Close button */}
              <button
                onClick={handleCloseSidebar}
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.15)",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.25)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.15)")}
              >
                <X size={16} />
              </button>

              {/* Ticket ID + Status Row */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, position: "relative" }}>
                <span
                  style={{
                    fontFamily: "monospace",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "rgba(255,255,255,0.65)",
                    letterSpacing: "0.05em",
                  }}
                >
                  {selectedTicket.id}
                </span>
                {(() => {
                  const s = statusConfig[selectedTicket.status] || statusConfig.open;
                  return (
                    <span
                      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${s.styles}`}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: s.dot,
                          display: "inline-block",
                          marginRight: 5,
                        }}
                      />
                      {s.label}
                    </span>
                  );
                })()}
              </div>

              {/* Title */}
              <h2
                style={{
                  color: "#ffffff",
                  fontSize: 18,
                  fontWeight: 700,
                  lineHeight: 1.35,
                  margin: "0 0 16px",
                  position: "relative",
                  paddingRight: 40,
                }}
              >
                {selectedTicket.subject}
              </h2>

              {/* Meta pills */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", position: "relative" }}>
                {selectedTicket.category && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      background: "rgba(255,255,255,0.12)",
                      borderRadius: 999,
                      padding: "4px 10px",
                      fontSize: 12,
                      color: "rgba(255,255,255,0.85)",
                    }}
                  >
                    <Tag size={11} />
                    {selectedTicket.category}
                  </div>
                )}
                {selectedTicket.createdAt && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      background: "rgba(255,255,255,0.12)",
                      borderRadius: 999,
                      padding: "4px 10px",
                      fontSize: 12,
                      color: "rgba(255,255,255,0.85)",
                    }}
                  >
                    <Calendar size={11} />
                    Created {selectedTicket.createdAt}
                  </div>
                )}
              </div>
            </div>

            {/* Body */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "24px",
                background: "#fafafa",
              }}
            >
              {/* Description */}
              {selectedTicket.description ? (
                <div style={{ marginBottom: 24 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      marginBottom: 10,
                    }}
                  >
                    <FileText size={14} color="#3B0069" />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        color: "#3B0069",
                      }}
                    >
                      Description
                    </span>
                  </div>
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #e9e3f5",
                      borderRadius: 12,
                      padding: "14px 16px",
                      fontSize: 13.5,
                      color: "#374151",
                      lineHeight: 1.65,
                      boxShadow: "0 1px 4px rgba(59,0,105,0.05)",
                    }}
                  >
                    {selectedTicket.description}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    marginBottom: 24,
                    background: "#fff",
                    border: "1px dashed #ddd6f5",
                    borderRadius: 12,
                    padding: "20px 16px",
                    textAlign: "center",
                  }}
                >
                  <FileText size={22} color="#c4b5e8" style={{ margin: "0 auto 8px" }} />
                  <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>No description provided</p>
                </div>
              )}

              {/* Attachments */}
              {selectedTicket.attachments && selectedTicket.attachments.length > 0 ? (
                <div style={{ marginBottom: 24 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      marginBottom: 10,
                    }}
                  >
                    <Paperclip size={14} color="#3B0069" />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        color: "#3B0069",
                      }}
                    >
                      Attachments
                    </span>
                    <span
                      style={{
                        background: "#ede8f8",
                        color: "#3B0069",
                        fontSize: 11,
                        fontWeight: 600,
                        borderRadius: 999,
                        padding: "1px 8px",
                      }}
                    >
                      {selectedTicket.attachments.length}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {selectedTicket.attachments.map((file, index) => (
                      <div key={index}>
                        {isImageFile(file) && (
                          <a
                            href={file}
                            target="_blank"
                            rel="noreferrer"
                            style={{ display: "block", marginBottom: 8 }}
                          >
                            <div
                              style={{
                                width: "100%",
                                borderRadius: 10,
                                overflow: "hidden",
                                border: "1px solid #e9e3f5",
                                background: "#f5f0fe",
                                maxHeight: 300,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 1px 6px rgba(59,0,105,0.08)",
                              }}
                            >
                              <img
                                src={file}
                                alt={getAttachmentLabel(file)}
                                style={{
                                  width: "100%",
                                  maxHeight: 300,
                                  display: "block",
                                }}
                                onError={(e) => {
                                  e.currentTarget.parentElement.parentElement.style.display = "none";
                                }}
                              />
                            </div>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    marginBottom: 24,
                    background: "#fff",
                    border: "1px dashed #ddd6f5",
                    borderRadius: 12,
                    padding: "20px 16px",
                    textAlign: "center",
                  }}
                >
                  <Paperclip size={22} color="#c4b5e8" style={{ margin: "0 auto 8px" }} />
                  <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>No attachments</p>
                </div>
              )}

              {/* Type your reply — commented out */}
              {/* <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 20, marginTop: 8 }}>
                <textarea
                  placeholder="Type your reply..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  style={{
                    width: "100%",
                    minHeight: 110,
                    border: "1px solid #d1d5db",
                    borderRadius: 12,
                    padding: "12px 14px",
                    fontSize: 13,
                    resize: "vertical",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                  <button>
                    <Paperclip size={14} />
                    Attach File
                  </button>
                  <button onClick={handleSendReply} disabled={!replyText.trim()}>
                    <MessageSquare size={14} />
                    Send Reply
                  </button>
                </div>
              </div> */}

              {/* Activity Log — commented out */}
              {/* <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid #e5e7eb" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 14 }}>
                  <Clock size={14} color="#3B0069" />
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#3B0069" }}>
                    Activity Log
                  </span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#3b82f6", flexShrink: 0 }} />
                    <span style={{ flex: 1, color: "#6b7280" }}>Ticket created</span>
                    <span style={{ fontSize: 12, color: "#9ca3af" }}>{selectedTicket.createdAt}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#eab308", flexShrink: 0 }} />
                    <span style={{ flex: 1, color: "#6b7280" }}>Status updated</span>
                    <span style={{ fontSize: 12, color: "#9ca3af" }}>{selectedTicket.updatedAt}</span>
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </>
      )}
    </div>
  );
}